import api from '../utils/api';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PayrollExport } from './StaffHomePage';

const getIn = (s) => s.clock_in || s.clock_in_at || s.start_time || s.started_at || s.in_time || null;
const getOut = (s) => s.clock_out || s.clock_out_at || s.end_time || s.ended_at || s.out_time || null;
const workerName = (s) => {
  if (s.worker) return `${s.worker.first_name || ''} ${s.worker.last_name || ''}`.trim() || 'Staff';
  return s.worker_name || s.name || 'Staff';
};
const workerKey = (s) => (s.worker && (s.worker.id || s.worker.email)) || s.account_id || s.membership_id || workerName(s);
const jobTitle = (s) => (s.worker && (s.worker.job_title || s.job_title)) || s.job_title || '';
const fmtTime = (d) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const fmtDay = (d) => d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
const fmtDur = (ms) => {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};
const weekStart = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
};

function isBarnOwner() {
  try {
    const u = JSON.parse(localStorage.getItem('cr_user') || '{}');
    const bag = [u.primary_role, u.account_type, u.roles, u.role,
      u.user && u.user.primary_role, u.user && u.user.account_type, u.user && u.user.roles]
      .filter(Boolean).join(' ');
    return bag.includes('barn_owner') || bag.includes('platform_admin');
  } catch (e) { return false; }
}

const WeekBar = ({ hours }) => {
  const pct = Math.min(100, Math.round((hours / 40) * 100));
  const hot = hours >= 40, warm = hours >= 34;
  const color = hot ? '#A32D2D' : warm ? '#B4552E' : '#1D9E75';
  const label = hot ? `${hours.toFixed(1)}h · OVERTIME +${(hours - 40).toFixed(1)}h`
    : warm ? `${hours.toFixed(1)}h · OT in ${(40 - hours).toFixed(1)}h`
    : `${hours.toFixed(1)}h this week`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
      <div style={{ flex: 1, height: 6, background: '#F1EAE0', borderRadius: 99 }}>
        <div style={{ width: pct + '%', height: 6, background: color, borderRadius: 99 }} />
      </div>
      <span style={{ fontSize: 11.5, whiteSpace: 'nowrap', color: warm || hot ? color : 'var(--text-secondary)', fontWeight: warm || hot ? 600 : 400 }}>{label}</span>
    </div>
  );
};

const PayrollPage = () => {
  const navigate = useNavigate();
  const [org, setOrg] = useState(undefined);
  const [shifts, setShifts] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    let ok = true;
    const run = async () => {
      for (const ep of ['/organizations/mine', '/organizations', '/memberships/mine', '/staff/organizations']) {
        try {
          const r = await api.get(ep);
          const list = Array.isArray(r.data) ? r.data : (r.data.organizations || r.data.orgs || []);
          if (list.length) {
            const managed = list.find(o => o.my_role === 'manager' || o.role === 'manager') || list[0];
            if (ok) setOrg(managed.organization || managed);
            return;
          }
        } catch (e) {}
      }
      if (ok) setOrg(null);
    };
    run();
    return () => { ok = false; };
  }, []);

  const oid = org && org.id;
  useEffect(() => {
    if (!oid) return;
    let ok = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/organizations/${oid}/shifts`);
        if (ok) setShifts(Array.isArray(data) ? data : (data.shifts || []));
      } catch (e) { if (ok) setShifts([]); }
    };
    load();
    const refetch = setInterval(load, 120000);
    const ticker = setInterval(() => setTick(t => t + 1), 30000);
    return () => { ok = false; clearInterval(refetch); clearInterval(ticker); };
  }, [oid]);

  if (!isBarnOwner()) { return <div className="page-content"><div className="card" style={{ padding: 14, marginTop: 20 }}>Payroll is available to barn owners and managers.</div></div>; }
  if (org === undefined || (oid && shifts === null)) return <div className="loading-screen"><div className="spinner"></div></div>;

  const now = Date.now();
  const ws = weekStart().getTime();
  const weekMs = {}, lastDone = {}, meta = {};
  const open = [];
  (shifts || []).forEach(s => {
    const inRaw = getIn(s), outRaw = getOut(s);
    if (!inRaw) return;
    const tIn = new Date(inRaw).getTime();
    const tOut = outRaw ? new Date(outRaw).getTime() : null;
    if (isNaN(tIn)) return;
    const k = workerKey(s);
    meta[k] = { name: workerName(s), title: jobTitle(s) };
    if (!tOut) open.push({ k, tIn });
    else {
      const prev = lastDone[k];
      if (!prev || tOut > prev.tOut) lastDone[k] = { tIn, tOut };
    }
    const spanEnd = tOut || now;
    if (spanEnd > ws) weekMs[k] = (weekMs[k] || 0) + (spanEnd - Math.max(tIn, ws));
  });
  const offKeys = Object.keys(meta).filter(k => !open.find(o => o.k === k));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 90 }}>
      <div className="top-bar" style={{ padding: '18px 16px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/staff')} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>&#8249;</button>
          <div>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', margin: 0, fontWeight: 600 }}>{org ? org.name : 'Your barn'}</p>
            <h1 style={{ color: 'white', fontSize: 24, margin: '3px 0 0' }}>Payroll</h1>
          </div>
        </div>
      </div>
      <div className="page-content">
        <div className="section-label">On the clock now</div>
        {open.length === 0 && (
          <div className="card" style={{ padding: '13px 15px', marginBottom: 8 }}>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>All quiet {'\u2014'} nobody on the clock.</div>
          </div>
        )}
        {open.map(({ k, tIn }) => (
          <div key={k} className="card" style={{ padding: '12px 14px', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#1D9E75', flex: 'none' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{meta[k].name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{meta[k].title ? meta[k].title + ' · ' : ''}in since {fmtTime(new Date(tIn))}</div>
              </div>
              <span style={{ fontWeight: 600, fontSize: 15, color: '#4B002B' }}>{fmtDur(now - tIn)}</span>
            </div>
            <WeekBar hours={(weekMs[k] || 0) / 3600000} />
          </div>
        ))}
        <div className="section-label" style={{ marginTop: 14 }}>Off the clock</div>
        {offKeys.length === 0 && (
          <div className="card" style={{ padding: '13px 15px', marginBottom: 8 }}>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>No completed shifts on record yet.</div>
          </div>
        )}
        {offKeys.map(k => {
          const ld = lastDone[k];
          return (
            <div key={k} className="card" style={{ padding: '11px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#D8CFC4', flex: 'none' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{meta[k].name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {ld ? `Last worked ${fmtDay(new Date(ld.tOut))} · ${fmtDur(ld.tOut - ld.tIn)}` : 'No completed shifts yet'}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{((weekMs[k] || 0) / 3600000).toFixed(1)}h this week</span>
              </div>
            </div>
          );
        })}
        <button className="card" style={{ width: '100%', textAlign: 'left', cursor: 'pointer', padding: '11px 14px', display: 'flex', alignItems: 'center', border: '1px solid var(--border-color)', marginTop: 4 }} onClick={() => navigate('/staff-work')}>
          <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>Approve and edit time cards</span>
          <span style={{ color: '#A97B4F', fontSize: 18 }}>&#8250;</span>
        </button>
        <div className="section-label" style={{ marginTop: 16 }}>Export payroll</div>
        {oid && <PayrollExport org={{ id: oid }} />}
      </div>
    </div>
  );
};

export default PayrollPage;
